<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import QaDashboardApi from 'dashboard/api/qaDashboard';
// Same URL builder My QA and the Live Monitor use, so all three open the same
// place from a conversation number.
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import '../styles/qaShell.css';

// QA_FE_INSIGHT_013B_V1
const { t } = useI18n();
const route = useRoute();
const showAlert = useAlert;

const isLoading = ref(false);
const calibrationOnly = ref(true);
const schemaVersion = ref('');
const source = ref('');
const denominator = ref('customer_turns');
const customerTurns = ref(0);
const eligibleTotal = ref(0);
const processedCount = ref(0);
const truncated = ref(false);
const processingCap = ref(null);
const coverageStatus = ref('complete');
const watermark = ref(null);
const sourceMetadata = ref(null);
const minSample = ref(3);
const topics = ref([]);
const centroidMatch = ref(null);
const clusterLineage = ref(null);
const topicNaming = ref(null);
const trends = ref(null);
const painPoints = ref(null);
const kbGaps = ref(null);
const guards = reactive({
  live_embed_invoked: false,
  llm_invoked: false,
  invented_external_facts: false,
});
const capabilities = reactive({
  default_live_embed: false,
  llm_topic_naming: false,
  invented_external_facts: false,
  denominator: 'customer_turns',
  trend_comparator: null,
});

const toLocalInput = date => {
  const d = new Date(date);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// A day, not seven. There is no pre-computed store behind this page: every
// load hashes and PII-redacts each customer turn in the window, and a week of
// that exceeded the 15s request timeout on every attempt — 13 for 13 in the
// production log — so the page never finished loading at all.
//
// Measured on this account after the cache write was folded into one bulk
// upsert: 12h = 1.8s, 24h = 2.7s, 3d = 6.6s, 7d = 14.4s. A day holds 82% of
// the budget spare, and 66% even with the comparison on. A week clears the
// limit by 4% with the comparison off and blows straight through it with the
// comparison on, so it stays out of reach until the aggregation moves off the
// request path — the ceiling still scales with how busy the account is.
const filters = reactive({
  window_start: toLocalInput(Date.now() - 24 * 60 * 60 * 1000),
  window_end: toLocalInput(Date.now()),
  min_sample: 3,
  // Off by default: the comparison re-runs the whole aggregation over the
  // preceding window, so it roughly doubles the request. At the default day
  // that is 2.7s against 5.1s — both fine — but it is what puts a week out of
  // reach, so it stays something you ask for rather than something you pay for
  // every load.
  compare_prior: false,
});

// What the last completed request actually asked for. Read instead of
// filters.compare_prior so toggling the box without pressing Apply cannot
// relabel results already on screen.
const comparedPrior = ref(false);

const pct = value => {
  if (value === null || value === undefined || value === '') return '—';
  return `${(Number(value) * 100).toFixed(1)}%`;
};

const num = value => {
  if (value === null || value === undefined || value === '') return '—';
  return Number(value);
};

const rankedTopics = computed(() =>
  (topics.value || []).filter(row => row.topic_key !== 'other_unknown')
);

// The list is one row per distinct customer message, sorted by how often it
// was sent, and the tail is long and thin: a day of this account produces 56
// rows, three days 147, a week 257 — all rendered at once, with a sample line
// and a share bar each. The head is the part anyone reads, so only the head is
// drawn and the rest is a click away. Nothing is dropped: rankedTopics.length
// still feeds the "Exact topics" tile, and the counts below say exactly what
// is still folded up.
const TOPIC_PAGE = 25;
const topicLimit = ref(TOPIC_PAGE);
const visibleTopics = computed(() =>
  rankedTopics.value.slice(0, topicLimit.value)
);
const hiddenTopics = computed(() => rankedTopics.value.slice(topicLimit.value));
const hiddenTopicTurns = computed(() =>
  hiddenTopics.value.reduce((sum, row) => sum + (Number(row.count) || 0), 0)
);
const showMoreTopics = () => {
  topicLimit.value += TOPIC_PAGE;
};

const otherBucket = computed(
  () =>
    (topics.value || []).find(row => row.topic_key === 'other_unknown') || null
);

const namingByKey = computed(() => {
  const map = {};
  const rows = topicNaming.value?.topics || [];
  rows.forEach(row => {
    if (row?.topic_key) map[row.topic_key] = row.display_name;
  });
  return map;
});

const trendSeries = computed(() =>
  trends.value?.available ? (trends.value.series || []).slice(0, 12) : []
);

const sourceLabel = computed(() => {
  if (source.value === 'messages_fallback') {
    return t('QA_DASHBOARD.CUSTOMER_INSIGHT.SOURCE_MESSAGES_FALLBACK');
  }
  if (source.value === 'fact_turns') {
    return t('QA_DASHBOARD.CUSTOMER_INSIGHT.SOURCE_FACT_TURNS');
  }
  return source.value || '—';
});

// TopicNamer is dormant — naming a bucket needs an LLM and that is off by
// design — so it hands back a placeholder built from the row's own fingerprint:
// "Topic 5c8e5c61", or the topic_key when there is no digest. Those are
// identifiers, not names, and putting one where a heading goes told the reader
// nothing while pushing the message itself into a footnote.
//
// Recomputed from the row rather than matched by pattern, so this recognises
// exactly what the server generates and nothing that merely looks like it.
const isPlaceholderName = (row, name) => {
  const digest = String(row.exact_digest || '');
  if (digest && name === `Topic ${digest.slice(0, 8)}`) return true;
  return Boolean(row.topic_key) && name === row.topic_key;
};

const namedTopicTitle = row => {
  const name = namingByKey.value[row.topic_key] || row.display_name || '';
  if (!name || isPlaceholderName(row, name)) return '';
  return name;
};

// Long enough to fill the two lines the sample-as-heading style clamps to.
const TOPIC_TITLE_MAX = 240;

const cleanSample = row =>
  String(row.sample_redacted || '')
    .trim()
    .replace(/\s+/g, ' ');

const topicTitle = row => {
  const named = namedTopicTitle(row);
  if (named) return named;
  const sample = cleanSample(row);
  if (sample) return sample.slice(0, TOPIC_TITLE_MAX);
  return row.topic_key || '—';
};

const topicTitleIsSample = row =>
  !namedTopicTitle(row) && Boolean(String(row.sample_redacted || '').trim());

// A bucket is one message sent many times, and almost never by one person: 51
// of 53 buckets here spanned more than one conversation and the busiest spanned
// 140. So the row carries a count and a sample of the newest chats behind it,
// never a single reference that would read as "this bucket is that chat".
const conversationCount = row => Number(row.conversation_count) || 0;
const conversationIds = row => row.conversation_display_ids || [];
const hiddenConversationCount = row =>
  Math.max(0, conversationCount(row) - conversationIds(row).length);

const conversationLink = displayId =>
  frontendURL(
    conversationUrl({ accountId: route.params?.accountId, id: displayId })
  );

const shareBarWidth = share => {
  const n = Number(share);
  if (!Number.isFinite(n)) return 0;
  return Math.max(2, Math.min(100, n * 100));
};

const trendDirectionLabel = direction => {
  if (direction === 'rising') {
    return t('QA_DASHBOARD.CUSTOMER_INSIGHT.TREND_RISING');
  }
  if (direction === 'falling') {
    return t('QA_DASHBOARD.CUSTOMER_INSIGHT.TREND_FALLING');
  }
  if (direction === 'stable') {
    return t('QA_DASHBOARD.CUSTOMER_INSIGHT.TREND_STABLE');
  }
  return direction || '—';
};

const trendDirectionArrow = direction => {
  if (direction === 'rising') return '▲';
  if (direction === 'falling') return '▼';
  return '●';
};

const trendDirectionClass = direction => {
  if (direction === 'rising') return 'qa-trend-chip qa-trend-chip--up';
  if (direction === 'falling') return 'qa-trend-chip qa-trend-chip--down';
  return 'qa-trend-chip qa-trend-chip--flat';
};

const loadInsight = async () => {
  isLoading.value = true;
  try {
    const requestedPrior = filters.compare_prior === true;
    const params = {
      window_start: new Date(filters.window_start).toISOString(),
      window_end: new Date(filters.window_end).toISOString(),
      min_sample: Number(filters.min_sample) || 3,
      include_trends: requestedPrior,
    };
    const response = await QaDashboardApi.getCustomerInsight(params);
    comparedPrior.value = requestedPrior;
    // A new window is a new list; leaving it expanded would carry one query's
    // reading position onto another query's results.
    topicLimit.value = TOPIC_PAGE;
    const data = response?.data || {};
    calibrationOnly.value = data.calibration_only !== false;
    schemaVersion.value = data.schema_version || '';
    source.value = data.source || '';
    denominator.value = data.denominator || 'customer_turns';
    customerTurns.value = data.customer_turns || 0;
    eligibleTotal.value = data.eligible_total ?? data.customer_turns ?? 0;
    processedCount.value = data.processed_count ?? data.customer_turns ?? 0;
    truncated.value = data.truncated === true;
    processingCap.value = data.processing_cap ?? null;
    coverageStatus.value =
      data.coverage_status || (truncated.value ? 'degraded' : 'complete');
    watermark.value = data.watermark || null;
    sourceMetadata.value = data.source_metadata || null;
    minSample.value = data.min_sample || params.min_sample;
    topics.value = Array.isArray(data.topics) ? data.topics : [];
    centroidMatch.value = data.centroid_match || null;
    clusterLineage.value = data.cluster_lineage || null;
    topicNaming.value = data.topic_naming || null;
    trends.value = data.trends || null;
    painPoints.value = data.pain_points || null;
    kbGaps.value = data.kb_gaps || null;
    Object.assign(guards, data.guards || {});
    Object.assign(capabilities, data.capabilities || {});
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.CUSTOMER_INSIGHT.ERROR_LOAD')
    );
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadInsight);
</script>

<template>
  <div class="qa-page">
    <div class="qa-shell">
      <div class="qa-header">
        <div class="qa-header-left">
          <div class="qa-title-row">
            <h1 class="qa-title">
              {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.TITLE') }}
            </h1>
            <span v-if="calibrationOnly" class="qa-badge qa-badge--warn">
              {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.CALIBRATION_BADGE') }}
            </span>
            <span v-if="!truncated" class="qa-badge qa-badge--good">
              {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.COVERAGE_COMPLETE') }}
            </span>
          </div>
          <p class="qa-desc">
            {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.DESCRIPTION') }}
          </p>
        </div>
        <div class="qa-header-actions">
          <button
            type="button"
            class="qa-btn"
            :disabled="isLoading"
            @click="loadInsight"
          >
            {{
              isLoading
                ? t('QA_DASHBOARD.CUSTOMER_INSIGHT.LOADING')
                : t('QA_DASHBOARD.CUSTOMER_INSIGHT.REFRESH')
            }}
          </button>
        </div>
      </div>

      <div v-if="truncated" class="qa-banner qa-banner--danger" role="alert">
        {{
          t('QA_DASHBOARD.CUSTOMER_INSIGHT.COVERAGE_TRUNCATED', {
            processed: processedCount,
            eligible: eligibleTotal,
            cap: processingCap,
          })
        }}
      </div>

      <div class="qa-toolbar">
        <div class="qa-toolbar-left">
          <label class="qa-field">
            <span class="qa-label">{{
              t('QA_DASHBOARD.CUSTOMER_INSIGHT.WINDOW_START')
            }}</span>
            <input
              v-model="filters.window_start"
              type="datetime-local"
              class="qa-input"
            />
          </label>
          <label class="qa-field">
            <span class="qa-label">{{
              t('QA_DASHBOARD.CUSTOMER_INSIGHT.WINDOW_END')
            }}</span>
            <input
              v-model="filters.window_end"
              type="datetime-local"
              class="qa-input"
            />
          </label>
          <label class="qa-field">
            <span class="qa-label">{{
              t('QA_DASHBOARD.CUSTOMER_INSIGHT.MIN_SAMPLE')
            }}</span>
            <input
              v-model.number="filters.min_sample"
              type="number"
              min="1"
              max="100"
              class="qa-input"
            />
          </label>
          <label class="qa-field qa-field--check">
            <span class="qa-label">{{
              t('QA_DASHBOARD.CUSTOMER_INSIGHT.COMPARE_PRIOR')
            }}</span>
            <span class="qa-check-row">
              <input v-model="filters.compare_prior" type="checkbox" />
              <span class="qa-hint">{{
                t('QA_DASHBOARD.CUSTOMER_INSIGHT.COMPARE_PRIOR_HINT')
              }}</span>
            </span>
          </label>
          <button
            type="button"
            class="qa-btn qa-btn--primary"
            :disabled="isLoading"
            @click="loadInsight"
          >
            {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.APPLY') }}
          </button>
        </div>
      </div>

      <div class="qa-content">
        <div class="qa-kpis">
          <div class="qa-kpi qa-kpi--tone-accent">
            <div class="qa-kpi-label">
              {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.CUSTOMER_TURNS') }}
            </div>
            <div class="qa-kpi-value">{{ num(customerTurns) }}</div>
            <div class="qa-kpi-sub">
              {{
                t('QA_DASHBOARD.CUSTOMER_INSIGHT.COVERAGE_COUNTS', {
                  processed: processedCount,
                  eligible: eligibleTotal,
                })
              }}
            </div>
          </div>
          <div
            class="qa-kpi"
            :class="truncated ? 'qa-kpi--tone-bad' : 'qa-kpi--tone-good'"
          >
            <div class="qa-kpi-label">
              {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.COVERAGE') }}
            </div>
            <div
              class="qa-kpi-value"
              :class="truncated ? 'qa-kpi-value--danger' : 'qa-kpi-value--ok'"
            >
              {{
                truncated
                  ? t('QA_DASHBOARD.CUSTOMER_INSIGHT.COVERAGE_DEGRADED')
                  : t('QA_DASHBOARD.CUSTOMER_INSIGHT.COVERAGE_COMPLETE')
              }}
            </div>
            <div class="qa-kpi-sub">
              {{ coverageStatus }}
            </div>
          </div>
          <div class="qa-kpi qa-kpi--tone-neutral">
            <div class="qa-kpi-label">
              {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.TOPICS_SHOWN') }}
            </div>
            <div class="qa-kpi-value">{{ rankedTopics.length }}</div>
            <div class="qa-kpi-sub">
              {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.MIN_SAMPLE') }} ≥
              {{ minSample }}
            </div>
          </div>
        </div>

        <section class="qa-card">
          <div class="qa-card-header">
            <h2 class="qa-card-title">
              {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.TOPICS') }}
            </h2>
          </div>
          <div class="qa-card-body">
            <p class="qa-desc">
              {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.TOPICS_NOTE') }}
            </p>
            <div v-if="!rankedTopics.length" class="qa-empty">
              {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.EMPTY') }}
            </div>
            <div v-else class="qa-rank-list">
              <div
                v-for="(row, index) in visibleTopics"
                :key="row.topic_key || row.exact_digest"
                class="qa-rank-row"
                :class="{ 'qa-rank-row--top': index === 0 }"
              >
                <span class="qa-rank-index">{{ index + 1 }}</span>
                <div class="qa-rank-main">
                  <span
                    class="qa-rank-title"
                    :class="{
                      'qa-rank-title--sample': topicTitleIsSample(row),
                    }"
                    :title="topicTitle(row)"
                  >
                    {{ topicTitle(row) }}
                  </span>
                  <p
                    v-if="!topicTitleIsSample(row) && row.sample_redacted"
                    class="qa-rank-sample"
                  >
                    {{ row.sample_redacted }}
                  </p>
                  <details v-if="conversationCount(row)" class="qa-rank-convs">
                    <summary>
                      {{
                        t('QA_DASHBOARD.CUSTOMER_INSIGHT.CONVERSATIONS', {
                          count: conversationCount(row),
                        })
                      }}
                    </summary>
                    <div class="qa-conv-list">
                      <a
                        v-for="displayId in conversationIds(row)"
                        :key="displayId"
                        :href="conversationLink(displayId)"
                        target="_blank"
                        rel="noopener"
                        class="qa-link"
                      >
                        #{{ displayId }}
                      </a>
                      <span v-if="hiddenConversationCount(row)" class="qa-hint">
                        {{
                          t(
                            'QA_DASHBOARD.CUSTOMER_INSIGHT.CONVERSATIONS_MORE',
                            { count: hiddenConversationCount(row) }
                          )
                        }}
                      </span>
                      <span class="qa-rank-key" :title="row.topic_key">
                        {{ row.topic_key }}
                      </span>
                    </div>
                  </details>
                  <span v-else class="qa-rank-key" :title="row.topic_key">
                    {{ row.topic_key }}
                  </span>
                </div>
                <div class="qa-rank-stats">
                  <span class="qa-rank-count">{{ num(row.count) }}</span>
                  <span class="qa-rank-share">{{ pct(row.share) }}</span>
                </div>
                <span class="qa-progress qa-progress--wide qa-rank-bar">
                  <span
                    class="qa-progress-fill"
                    :style="{ width: `${shareBarWidth(row.share)}%` }"
                  />
                </span>
              </div>
            </div>
            <div v-if="hiddenTopics.length" class="qa-more-row">
              <button
                type="button"
                class="qa-btn qa-btn--sm qa-btn--ghost"
                @click="showMoreTopics"
              >
                {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.SHOW_MORE') }}
              </button>
              <span class="qa-hint">
                {{
                  t('QA_DASHBOARD.CUSTOMER_INSIGHT.SHOW_MORE_HINT', {
                    hidden: hiddenTopics.length,
                    turns: hiddenTopicTurns,
                  })
                }}
              </span>
            </div>
            <p v-if="otherBucket" class="qa-hint" style="margin-top: 10px">
              {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.OTHER_UNKNOWN') }}:
              {{ num(otherBucket.count) }} · {{ pct(otherBucket.share) }}
            </p>
          </div>
        </section>

        <section class="qa-card">
          <div class="qa-card-header">
            <h2 class="qa-card-title">
              {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.TRENDS') }}
            </h2>
          </div>
          <div class="qa-card-body" style="overflow-x: auto">
            <p class="qa-desc">
              {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.TRENDS_NOTE') }}
            </p>
            <div
              v-if="trends?.available"
              class="qa-chip-row"
              style="margin-top: 0; margin-bottom: 12px"
            >
              <span class="qa-trend-chip qa-trend-chip--up">
                ▲ {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.TREND_RISING') }}
                {{ trends.rising || 0 }}
              </span>
              <span class="qa-trend-chip qa-trend-chip--down">
                ▼ {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.TREND_FALLING') }}
                {{ trends.falling || 0 }}
              </span>
              <span class="qa-trend-chip qa-trend-chip--flat">
                ● {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.TREND_STABLE') }}
                {{ trends.stable || 0 }}
              </span>
            </div>
            <table v-if="trendSeries.length" class="qa-table qa-table--rich">
              <thead>
                <tr>
                  <th>{{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.COL_KEY') }}</th>
                  <th class="qa-num">
                    {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.COL_PRIOR') }}
                  </th>
                  <th class="qa-num">
                    {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.COL_CURRENT') }}
                  </th>
                  <th>{{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.COL_DELTA') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in trendSeries" :key="row.topic_key">
                  <td class="qa-mono">{{ row.topic_key }}</td>
                  <td class="qa-num">{{ num(row.prior_count) }}</td>
                  <td class="qa-num">{{ num(row.current_count) }}</td>
                  <td>
                    <span :class="trendDirectionClass(row.direction)">
                      {{ trendDirectionArrow(row.direction) }}
                      {{ trendDirectionLabel(row.direction) }}
                      <span>
                        {{ row.delta_count > 0 ? '+' : ''
                        }}{{ num(row.delta_count) }}
                      </span>
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
            <p v-else-if="!comparedPrior" class="qa-desc">
              {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.TRENDS_OFF') }}
            </p>
            <p
              v-else-if="trends?.reason === 'incomplete_window_coverage'"
              class="qa-desc qa-text-danger"
            >
              {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.TRENDS_DEGRADED') }}
            </p>
            <p v-else class="qa-desc">
              {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.TRENDS_EMPTY') }}
            </p>
          </div>
        </section>

        <details class="qa-disclosure">
          <summary>
            {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.DATA_QUALITY') }}
          </summary>

          <div class="qa-dq-grid">
            <div class="qa-dq-block">
              <div class="qa-kpi-label">
                {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.GUARDS') }}
              </div>
              <p class="qa-desc qa-mono">
                embed={{ guards.live_embed_invoked }} · llm={{
                  guards.llm_invoked
                }}
                · invent={{ guards.invented_external_facts }}
              </p>
            </div>

            <div class="qa-dq-block">
              <div class="qa-kpi-label">
                {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.CENTROID_MATCHED') }}
              </div>
              <p class="qa-desc">
                {{ num(centroidMatch?.matched_count) }}
                ·
                {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.CENTROID_PENDING') }}:
                {{ num(centroidMatch?.pending_pool_count) }}
              </p>
            </div>

            <div class="qa-dq-block">
              <p class="qa-desc qa-mono">
                {{
                  t('QA_DASHBOARD.CUSTOMER_INSIGHT.SCHEMA', {
                    version: schemaVersion || '—',
                  })
                }}
                · {{ sourceLabel }}
              </p>
              <p
                v-if="watermark?.processed_through?.started_at"
                class="qa-desc qa-mono"
              >
                {{
                  t('QA_DASHBOARD.CUSTOMER_INSIGHT.WATERMARK', {
                    at: watermark.processed_through.started_at,
                    id: watermark.processed_through.message_id,
                  })
                }}
                · {{ sourceMetadata?.ordering || '—' }}
              </p>
              <p v-else class="qa-desc qa-mono">
                {{ sourceMetadata?.ordering || '—' }}
              </p>
              <p v-if="clusterLineage?.schema_version" class="qa-desc qa-mono">
                lineage {{ clusterLineage.schema_version }}
              </p>
              <p class="qa-desc qa-mono">
                {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.DENOMINATOR') }}:
                {{ denominator }}
                ·
                {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.PROCESSING_CAP') }}:
                {{ num(processingCap) }}
              </p>
            </div>
          </div>

          <div class="qa-dq-block" style="margin-top: 12px">
            <div class="qa-kpi-label">
              {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.UNAVAILABLE') }}
            </div>
            <ul class="qa-unavailable-list">
              <li>
                {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.UNAVAILABLE_PAIN') }}
                <span v-if="painPoints" class="qa-mono">
                  · available={{ painPoints.available }}</span
                >
              </li>
              <li>
                {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.UNAVAILABLE_KB') }}
                <span v-if="kbGaps" class="qa-mono">
                  · available={{ kbGaps.available }}</span
                >
              </li>
              <li>{{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.UNAVAILABLE_LLM') }}</li>
              <li>
                {{ t('QA_DASHBOARD.CUSTOMER_INSIGHT.UNAVAILABLE_EMBED') }}
                (default_live_embed={{ capabilities.default_live_embed }})
              </li>
            </ul>
          </div>
        </details>
      </div>
    </div>
  </div>
</template>

<style scoped>
.qa-title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

/* qaShell.css holds .qa-rank-title to one ellipsised line, which is right for a
   short name. When the heading is the message itself there is no detail line
   under it to carry the rest, so it takes the two lines that line used to have.
   Two, not more: the row stays scannable and the full text is on the tooltip. */
.qa-rank-title--sample {
  white-space: normal;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.45;
}

/* Sits in the slot the bare topic_key used to occupy, so a folded row is the
   same height it was — the reference key moves inside, where it is still there
   for cross-referencing the trend table without taking a line of its own. */
.qa-rank-convs summary {
  font-size: 11px;
  color: var(--qa-text-muted);
  cursor: pointer;
  list-style: none;
  width: fit-content;
}

.qa-rank-convs summary::-webkit-details-marker {
  display: none;
}

.qa-rank-convs summary::before {
  content: '▸';
  display: inline-block;
  margin-right: 4px;
  font-size: 9px;
}

.qa-rank-convs[open] summary::before {
  content: '▾';
}

.qa-conv-list {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 10px;
  margin-top: 6px;
  padding-left: 12px;
}

.qa-conv-list .qa-link {
  font-size: 12px;
  white-space: nowrap;
}

.qa-more-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 12px;
}

/* qaShell.css stacks .qa-field as label-over-control, which suits the text and
   date inputs beside this one. A checkbox has to sit next to its explanation
   instead, and the field needs the height of an input so the row of controls
   still lines up along the bottom. */
.qa-field--check .qa-check-row {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 2.25rem;
}

.qa-field--check input[type='checkbox'] {
  width: 14px;
  height: 14px;
  margin: 0;
  flex: none;
  cursor: pointer;
}

.qa-field--check .qa-hint {
  white-space: nowrap;
}

.qa-header-left .qa-desc {
  margin-top: 6px;
}

.qa-banner {
  margin: 12px 16px 0;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
}

.qa-banner--danger {
  background: #fef2f2;
  color: #991b1b;
  border: 1px solid #fecaca;
}

.qa-kpi-sub {
  display: block;
  font-size: 11px;
  color: var(--qa-text-muted);
  font-weight: 400;
  margin-top: 2px;
}

.qa-kpi-value--ok {
  color: #166534;
}

.qa-kpi-value--danger,
.qa-text-danger {
  color: #991b1b;
}

.qa-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
}

.qa-topic-primary {
  font-weight: 600;
  font-size: 13px;
  color: var(--qa-text);
}

.qa-topic-key {
  margin-top: 2px;
  font-size: 11px;
  color: var(--qa-text-muted);
}

.qa-sample {
  max-width: 420px;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
}

.qa-empty {
  text-align: center;
  color: var(--qa-text-muted);
  padding: 24px;
}

.qa-unavailable-list {
  margin: 6px 0 0;
  padding-left: 18px;
  color: var(--qa-text-muted);
  font-size: 13px;
  line-height: 1.6;
}

.qa-dq-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
  padding-top: 4px;
}

.qa-dq-block .qa-kpi-label {
  margin-bottom: 4px;
}
</style>
